import { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router';
import { toast } from 'sonner';
import {
  useSeeds,
  useBulkDeleteSeeds,
  useBulkRestoreSeeds,
} from '@/hooks/use-seeds';
import { useAuthContext } from '@/contexts/auth-context';
import { SeedsTable } from '@/components/seeds/seeds-table';
import { SeedFilterPanel } from '@/components/seeds/seed-filter-panel';
import { FilterPresetsMenu } from '@/components/seeds/filter-presets-menu';
import { BulkActionsToolbar } from '@/components/seeds/bulk-actions-toolbar';
import { BulkUpdateDialog } from '@/components/seeds/bulk-update-dialog';
import { BulkAssignStudentsDialog } from '@/components/seeds/bulk-assign-students-dialog';
import { ImportSeedsDialog } from '@/components/seeds/import-seeds-dialog';
import { ExportSeedsMenu } from '@/components/seeds/export-seeds-menu';
import { AIGenerateSeedsDialog } from '@/components/seeds/ai-generate-seeds-dialog';
import { CreateSeedDialog } from '@/components/seeds/create-seed-dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Search, Upload, Sparkles } from 'lucide-react';
import { exportSeeds } from '@/api/seeds';
import type { PracticeSeedListParams, SeedListStatus } from '@/types/seeds';
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@/components/ui/pagination';

export default function SeedsPage() {
  const { user } = useAuthContext();
  const [searchParams, setSearchParams] = useSearchParams();

  const isAdmin = user?.role === 'admin';

  // ---------- Parse URL params into PracticeSeedListParams ----------
  const params = useMemo((): PracticeSeedListParams => {
    const categories = searchParams.get('categories');
    return {
      page: Number(searchParams.get('page') ?? '1'),
      per_page: Number(searchParams.get('per_page') ?? '20'),
      sort_by: searchParams.get('sort_by') ?? 'created_at',
      sort_order: searchParams.get('sort_order') ?? 'desc',
      search: searchParams.get('search') ?? undefined,
      categories: categories ? categories.split(',').filter(Boolean) : undefined,
      difficulty_min: numOrUndef(searchParams.get('difficulty_min')),
      difficulty_max: numOrUndef(searchParams.get('difficulty_max')),
      estimated_minutes_min: numOrUndef(searchParams.get('estimated_minutes_min')),
      estimated_minutes_max: numOrUndef(searchParams.get('estimated_minutes_max')),
      completion_rate_min: numOrUndef(searchParams.get('completion_rate_min')),
      completion_rate_max: numOrUndef(searchParams.get('completion_rate_max')),
      usage_mode: (searchParams.get('usage_mode') as PracticeSeedListParams['usage_mode']) ?? undefined,
      usage_threshold: numOrUndef(searchParams.get('usage_threshold')),
      created_from: searchParams.get('created_from') ?? undefined,
      created_to: searchParams.get('created_to') ?? undefined,
      updated_from: searchParams.get('updated_from') ?? undefined,
      updated_to: searchParams.get('updated_to') ?? undefined,
      status: (searchParams.get('status') as SeedListStatus) ?? 'active',
    };
  }, [searchParams]);

  const isTrashView = params.status === 'trash';

  // Debounced search input.
  const [searchInput, setSearchInput] = useState(params.search ?? '');
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        if (searchInput) {
          next.set('search', searchInput);
        } else {
          next.delete('search');
        }
        next.set('page', '1');
        return next;
      });
    }, 300);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);

  // ---------- Data ----------
  const { data, isLoading, refetch } = useSeeds(params);

  // ---------- Selection state ----------
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  // Build a stable fingerprint of every filter so the selection-clear effect
  // only re-runs when something actually changes.
  const filterKey = [
    params.page,
    params.search,
    params.status,
    params.sort_by,
    params.sort_order,
    params.categories?.join(','),
    params.difficulty_min,
    params.difficulty_max,
    params.estimated_minutes_min,
    params.estimated_minutes_max,
    params.completion_rate_min,
    params.completion_rate_max,
    params.usage_mode,
    params.usage_threshold,
    params.created_from,
    params.created_to,
    params.updated_from,
    params.updated_to,
  ].join('|');

  // Clear selection whenever filters or page change (SPEC-014 acceptance).
  useEffect(() => {
    setSelectedIds(new Set());
  }, [filterKey]);

  const toggleRow = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAllOnPage = (checked: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      const pageIds = data?.seeds.map((s) => s.id) ?? [];
      if (checked) {
        pageIds.forEach((id) => next.add(id));
      } else {
        pageIds.forEach((id) => next.delete(id));
      }
      return next;
    });
  };

  // "Select all N matching" pulls every id from the server that matches the
  // current filter, ignoring pagination. Uses the export endpoint to stay on
  // one round-trip, then extracts the id list client-side.
  const handleSelectAllMatching = async () => {
    try {
      const { blob } = await exportSeeds({ format: 'csv', filter_params: params });
      const text = await blob.text();
      const lines = text.split(/\r?\n/).slice(1).filter((l) => l.trim());
      const ids = lines
        .map((line) => line.split(',')[0]?.replace(/^"|"$/g, ''))
        .filter((x): x is string => !!x);
      setSelectedIds(new Set(ids));
      toast.success(`Selected ${ids.length} matching seeds`);
    } catch (e) {
      toast.error(`Select-all failed: ${(e as Error).message}`);
    }
  };

  // ---------- Bulk delete ----------
  const bulkDelete = useBulkDeleteSeeds();
  const bulkRestore = useBulkRestoreSeeds();

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    if (!confirm(`Delete ${selectedIds.size} selected seeds? Seeds with assignments will be skipped.`)) {
      return;
    }
    try {
      const result = await bulkDelete.mutateAsync({ ids: Array.from(selectedIds) });
      toast.success(
        `Deleted ${result.succeeded.length}${
          result.failed.length > 0 ? `, skipped ${result.failed.length} with dependencies` : ''
        }`,
      );
      setSelectedIds(new Set());
    } catch (e) {
      toast.error(`Bulk delete failed: ${(e as Error).message}`);
    }
  };

  const handleBulkRestore = async () => {
    if (selectedIds.size === 0) return;
    try {
      const result = await bulkRestore.mutateAsync({ ids: Array.from(selectedIds) });
      toast.success(`Restored ${result.restored} seeds`);
      setSelectedIds(new Set());
    } catch (e) {
      toast.error(`Bulk restore failed: ${(e as Error).message}`);
    }
  };

  // ---------- Dialog state ----------
  const [bulkUpdateOpen, setBulkUpdateOpen] = useState(false);
  const [bulkAssignOpen, setBulkAssignOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [aiGenerateOpen, setAIGenerateOpen] = useState(false);

  // ---------- Filter panel handlers ----------
  const applyPartial = (patch: Partial<PracticeSeedListParams>) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      for (const [k, v] of Object.entries(patch)) {
        if (v === undefined || v === null || v === '') {
          next.delete(k);
        } else if (Array.isArray(v)) {
          if (v.length === 0) next.delete(k);
          else next.set(k, v.join(','));
        } else {
          next.set(k, String(v));
        }
      }
      next.set('page', '1');
      return next;
    });
  };

  const clearAllFilters = () => {
    setSearchParams(new URLSearchParams());
    setSearchInput('');
  };

  const updatePage = (p: number) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('page', String(p));
      return next;
    });
  };

  const page = params.page ?? 1;
  const perPage = params.per_page ?? 20;
  const totalPages = data ? Math.ceil(data.total / perPage) : 0;

  return (
    <div className="space-y-4">
      {/* Top row: search, import/AI/export/create actions */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative w-64">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search seeds..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="pl-9"
          />
        </div>

        <FilterPresetsMenu
          currentParams={searchParams}
          onApply={(p) => setSearchParams(p)}
        />

        {isAdmin && !isTrashView && (
          <div className="ml-auto flex items-center gap-2">
            <ExportSeedsMenu filterParams={params} selectedIds={Array.from(selectedIds)} />
            <Button variant="outline" size="sm" onClick={() => setImportOpen(true)}>
              <Upload className="mr-2 h-4 w-4" />
              Import
            </Button>
            <Button variant="outline" size="sm" onClick={() => setAIGenerateOpen(true)}>
              <Sparkles className="mr-2 h-4 w-4" />
              AI Generate
            </Button>
            <CreateSeedDialog categories={data?.categories ?? []} />
          </div>
        )}
      </div>

      {/* Filter panel */}
      <SeedFilterPanel
        params={params}
        categories={data?.categories ?? []}
        onChange={applyPartial}
        onClear={clearAllFilters}
      />

      {/* Bulk toolbar (admin only) */}
      {isAdmin && (
        <BulkActionsToolbar
          selectedCount={selectedIds.size}
          totalMatching={data?.total ?? 0}
          onClearSelection={() => setSelectedIds(new Set())}
          onSelectAllMatching={handleSelectAllMatching}
          onBulkDelete={handleBulkDelete}
          onBulkUpdate={() => setBulkUpdateOpen(true)}
          onBulkAssign={() => setBulkAssignOpen(true)}
          onBulkExport={(format) => {
            exportSeeds({ format, selected_ids: Array.from(selectedIds) })
              .then(({ blob, filename }) => {
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = filename;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                URL.revokeObjectURL(url);
              })
              .catch((e) => toast.error(`Export failed: ${(e as Error).message}`));
          }}
          onBulkRestore={handleBulkRestore}
          isTrashView={isTrashView}
        />
      )}

      {/* Table */}
      {isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </div>
      ) : (
        <>
          <SeedsTable
            seeds={data?.seeds ?? []}
            selectable={isAdmin}
            selectedIds={selectedIds}
            onToggleRow={toggleRow}
            onToggleAllOnPage={toggleAllOnPage}
          />

          {totalPages > 1 && (
            <Pagination>
              <PaginationContent>
                <PaginationItem>
                  <PaginationPrevious
                    onClick={() => updatePage(Math.max(1, page - 1))}
                    aria-disabled={page <= 1}
                    className={page <= 1 ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                  />
                </PaginationItem>
                {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                  const p = i + 1;
                  return (
                    <PaginationItem key={p}>
                      <PaginationLink
                        isActive={p === page}
                        onClick={() => updatePage(p)}
                        className="cursor-pointer"
                      >
                        {p}
                      </PaginationLink>
                    </PaginationItem>
                  );
                })}
                <PaginationItem>
                  <PaginationNext
                    onClick={() => updatePage(Math.min(totalPages, page + 1))}
                    aria-disabled={page >= totalPages}
                    className={
                      page >= totalPages ? 'pointer-events-none opacity-50' : 'cursor-pointer'
                    }
                  />
                </PaginationItem>
              </PaginationContent>
            </Pagination>
          )}
        </>
      )}

      {/* Dialogs */}
      {isAdmin && (
        <>
          <BulkUpdateDialog
            open={bulkUpdateOpen}
            onOpenChange={setBulkUpdateOpen}
            selectedIds={Array.from(selectedIds)}
            categories={data?.categories ?? []}
            onSuccess={() => {
              setSelectedIds(new Set());
              refetch();
            }}
          />
          <BulkAssignStudentsDialog
            open={bulkAssignOpen}
            onOpenChange={setBulkAssignOpen}
            selectedSeedIds={Array.from(selectedIds)}
            onSuccess={() => {
              setSelectedIds(new Set());
              refetch();
            }}
          />
          <ImportSeedsDialog
            open={importOpen}
            onOpenChange={setImportOpen}
            onSuccess={refetch}
          />
          <AIGenerateSeedsDialog
            open={aiGenerateOpen}
            onOpenChange={setAIGenerateOpen}
            categories={data?.categories ?? []}
            onSuccess={refetch}
          />
        </>
      )}
    </div>
  );
}

function numOrUndef(s: string | null): number | undefined {
  if (s == null || s === '') return undefined;
  const n = Number(s);
  return Number.isNaN(n) ? undefined : n;
}
